<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useAttrs, watch } from 'vue';

defineOptions({ inheritAttrs: false, name: 'EGS_Dropdown' });
type Option = string | number | boolean | Record<string, unknown>;

const props = withDefaults(
	defineProps<{
		modelValue?: unknown;
		options?: Option[];
		optionLabel?: string;
		optionValue?: string;
		dataKey?: string;
		placeholder?: string;
		inputId?: string;
		disabled?: boolean;
		showClear?: boolean;
		filter?: boolean;
		filterable?: boolean;
		direction?: 'up' | 'down';
		size?: string;
		invalid?: boolean;
		hoverColor?: string;
		tabindex?: number;
	}>(),
	{
		options: () => [],
		optionLabel: 'label',
		optionValue: '',
		placeholder: 'Select',
		disabled: false,
		showClear: false,
		filter: false,
		filterable: false,
		direction: 'down',
		size: 'normal',
		invalid: false,
		tabindex: 0
	}
);
const emit = defineEmits<{
	'update:modelValue': [value: unknown];
	change: [event: { value: unknown; originalEvent?: Event | undefined }];
	input: [value: unknown];
}>();
const attrs = useAttrs();
const root = ref<HTMLElement | null>(null);
const menu = ref<HTMLElement | null>(null);
const open = ref(false);
const query = ref('');

/** Menu height cap; also the space needed below the trigger before the menu flips upward. */
const MENU_MAX_HEIGHT = 320;
const VIEWPORT_MARGIN = 8;

function getOptionValue(option: Option): unknown {
	return props.optionValue && typeof option === 'object' && option !== null ? option[props.optionValue] : option;
}
function getOptionLabel(option: Option): string {
	if (typeof option === 'object' && option !== null) return String(option[props.optionLabel] ?? option[props.optionValue] ?? '');
	return String(option);
}
function valuesMatch(optionValue: unknown, modelValue: unknown): boolean {
	if (Object.is(optionValue, modelValue)) return true;
	if (!props.dataKey || typeof optionValue !== 'object' || optionValue === null || typeof modelValue !== 'object' || modelValue === null) return false;
	return Object.is((optionValue as Record<string, unknown>)[props.dataKey], (modelValue as Record<string, unknown>)[props.dataKey]);
}

const selected = computed(() => props.options.find((option) => valuesMatch(getOptionValue(option), props.modelValue)));
const filtered = computed(() => {
	const needle = query.value.trim().toLowerCase();
	return needle ? props.options.filter((option) => getOptionLabel(option).toLowerCase().includes(needle)) : props.options;
});
/** aria-label given to the component lands on the trigger button, where screen readers look for it. */
const triggerLabel = computed(() => {
	const label = attrs['aria-label'] ?? attrs['ariaLabel'];
	return label ? { 'aria-label': String(label) } : {};
});
const sizeClass = computed(() => (props.size === 'sm' || props.size === 'small' ? 'min-h-8 px-2.5 text-xs' : 'min-h-10 px-3 text-sm'));

/**
 * The menu is position:fixed and placed from the trigger's viewport rect, so it escapes any scrolling or clipping
 * ancestor (dialog bodies, tables, cards) instead of being cut off at their edge. It opens downward unless there is
 * not enough room below (or `direction="up"` asks for it), and follows the trigger while the page scrolls.
 */
const menuStyle = ref<Record<string, string>>({});
const opensUpward = ref(false);
function place() {
	const trigger = root.value;
	if (!trigger) return;
	const rect = trigger.getBoundingClientRect();
	const roomBelow = window.innerHeight - rect.bottom - VIEWPORT_MARGIN;
	const roomAbove = rect.top - VIEWPORT_MARGIN;
	const wanted = Math.min(MENU_MAX_HEIGHT, menu.value?.offsetHeight || MENU_MAX_HEIGHT);
	opensUpward.value = props.direction === 'up' ? roomAbove >= Math.min(wanted, roomBelow) : roomBelow < wanted && roomAbove > roomBelow;
	const maxHeight = Math.max(120, Math.min(MENU_MAX_HEIGHT, opensUpward.value ? roomAbove : roomBelow));
	menuStyle.value = {
		left: `${rect.left}px`,
		width: `${rect.width}px`,
		maxHeight: `${maxHeight}px`,
		...(opensUpward.value ? { bottom: `${window.innerHeight - rect.top + 4}px` } : { top: `${rect.bottom + 4}px` })
	};
}

function choose(option: Option, event?: Event) {
	const value = getOptionValue(option);
	emit('update:modelValue', value);
	emit('change', { value, originalEvent: event });
	emit('input', value);
	open.value = false;
}
function clear(event: Event) {
	event.stopPropagation();
	emit('update:modelValue', null);
	emit('change', { value: null, originalEvent: event });
	emit('input', null);
}
function outside(event: MouseEvent) {
	const target = event.target as Node;
	if (root.value?.contains(target) || menu.value?.contains(target)) return;
	open.value = false;
}
function onViewportChange() {
	if (open.value) place();
}
watch(open, async (isOpen) => {
	if (!isOpen) {
		query.value = '';
		return;
	}
	await nextTick();
	place();
	// Filterable menus put the cursor in the search box so typing narrows the list right away.
	if (props.filter || props.filterable) menu.value?.querySelector('input')?.focus();
});
onMounted(() => {
	document.addEventListener('mousedown', outside);
	window.addEventListener('resize', onViewportChange);
	// Capture phase: scrolling inside a dialog or table never bubbles, so listen to every scroll in the document.
	document.addEventListener('scroll', onViewportChange, true);
});
onBeforeUnmount(() => {
	document.removeEventListener('mousedown', outside);
	window.removeEventListener('resize', onViewportChange);
	document.removeEventListener('scroll', onViewportChange, true);
});
</script>

<template>
	<div
		ref="root"
		v-bind="attrs"
		:tabindex="disabled ? -1 : tabindex"
		:class="[
			'relative inline-flex w-full items-center rounded-lg border bg-[var(--egs-content-background)] shadow-sm outline-none transition focus-within:border-emerald-500 focus-within:ring-2 focus-within:ring-emerald-200',
			disabled ? 'cursor-not-allowed text-color-secondary' : 'cursor-pointer text-color',
			invalid ? 'border-red-400' : 'border-surface',
			attrs['class']
		]"
		@keydown.esc="open = false"
	>
		<button
			type="button"
			:class="['flex min-w-0 flex-1 items-center text-left disabled:cursor-not-allowed', sizeClass]"
			:disabled="disabled"
			:aria-invalid="invalid"
			:aria-expanded="open"
			v-bind="inputId ? { id: inputId, ...triggerLabel } : triggerLabel"
			aria-haspopup="listbox"
			@click="open = !open"
		>
			<span v-if="selected == null" class="truncate text-color-secondary">{{ placeholder }}</span>
			<slot v-else name="value" :option="selected" :label="getOptionLabel(selected)"
				><span class="truncate">{{ getOptionLabel(selected) }}</span></slot
			>
		</button>
		<button
			v-if="showClear && modelValue != null"
			type="button"
			:disabled="disabled"
			class="mr-1 inline-flex h-7 w-7 items-center justify-center rounded-full text-color-secondary hover:bg-[var(--egs-content-hover)] disabled:cursor-not-allowed"
			aria-label="Clear selection"
			@click="clear"
		>
			<font-awesome-icon icon="xmark" />
		</button>
		<font-awesome-icon icon="chevron-down" class="pointer-events-none mr-3 h-3 w-3 text-color-secondary transition-transform" :class="open ? 'rotate-180' : ''" />

		<div
			v-if="open"
			ref="menu"
			:style="menuStyle"
			:class="['egs-dropdown-menu fixed z-[10001] flex flex-col overflow-hidden rounded-lg border border-surface bg-[var(--egs-overlay-background)] text-color shadow-dropdown', opensUpward ? 'egs-dropdown-menu--up' : 'egs-dropdown-menu--down']"
		>
			<div v-if="filter || filterable" class="shrink-0 border-b border-surface p-2">
				<input v-model="query" type="search" class="w-full rounded-md border border-surface bg-[var(--egs-content-background)] px-3 py-2 text-sm outline-none focus:border-emerald-500" placeholder="Search…" @click.stop />
			</div>
			<ul class="min-h-0 flex-1 overflow-auto p-1" role="listbox">
				<li v-for="(option, index) in filtered" :key="index">
					<button
						type="button"
						class="flex w-full items-center justify-between gap-3 rounded-md px-3 py-2 text-left text-sm transition hover:bg-[var(--egs-content-hover)]"
						:aria-selected="valuesMatch(getOptionValue(option), modelValue)"
						@click="choose(option, $event)"
					>
						<slot name="option" :option="option" :label="getOptionLabel(option)" :selected="valuesMatch(getOptionValue(option), modelValue)"
							><span class="truncate">{{ getOptionLabel(option) }}</span></slot
						>
						<font-awesome-icon v-if="valuesMatch(getOptionValue(option), modelValue)" icon="check" class="ml-3 h-3 w-3 shrink-0 text-primary" />
					</button>
				</li>
				<li v-if="!filtered.length" class="px-3 py-6 text-center text-sm text-color-secondary">No options found.</li>
			</ul>
		</div>
	</div>
</template>
